/*
 * Copyright (c) 2025 NoorWays. All rights reserved.
 * Licensed under the terms specified in the LICENSE.txt file.
 */

import { AuthApi, type ExchangeTokenRequest } from '@/api/backend'
import { UnauthenticatedBackendConfiguration } from '@/api/backend/base'

/**
 * Service for handling authentication operations that require special handling
 * This service provides methods that need custom configuration or logic
 * beyond the standard auto-generated API clients
 */
export class AuthService {
  private unauthenticatedAuthApi: AuthApi

  constructor() {
    // Create an unauthenticated API instance for operations that shouldn't include Bearer tokens
    this.unauthenticatedAuthApi = new AuthApi(UnauthenticatedBackendConfiguration)
  }

  /**
   * Exchange a redirect token for a full JWT token without sending authentication headers
   * This method is specifically designed to avoid 401 errors that can occur when
   * expired tokens are automatically included in requests
   * 
   * @param redirectToken The one-time redirect token to exchange
   * @returns Promise resolving to the authentication response
   */
  async exchangeRedirectToken(redirectToken: string) {
    const exchangeRequest: ExchangeTokenRequest = {
      redirectToken
    }

    // Use unauthenticated API to prevent automatic inclusion of Bearer tokens
    // This is crucial for the post-payment flow where users might have expired tokens
    return this.unauthenticatedAuthApi.exchangeRedirectToken(exchangeRequest)
  }
}

/**
 * Singleton instance of the AuthService
 */
export const authService = new AuthService()
